import { KanbanSquare, List, Plus } from "lucide-react";
import { useTranslate, type Identifier } from "ra-core";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import type { Task } from "../../types";
import {
  parseTaskFilters,
  setSearchParam,
  toTaskServerFilter,
} from "../filters";
import { getSaleName, useActiveSales } from "../useActiveSales";
import { TaskBoard } from "./TaskBoard";
import { TaskDialog } from "./TaskDialog";
import { TaskFilters } from "./TaskFilters";
import { TaskList } from "./TaskList";

type DialogState = { open: false } | { open: true; task?: Task };

export const TasksView = () => {
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "list" ? "list" : "board";
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const sales = useActiveSales();
  const salesNames = useMemo(
    () =>
      new Map<Identifier, string>(
        sales.map((sale) => [sale.id, getSaleName(sale)]),
      ),
    [sales],
  );

  // Recomputed only when the URL changes: the query keys (and so the
  // loaded pages) depend on this object
  const searchKey = searchParams.toString();
  const serverFilter = useMemo(
    () => {
      const filters = parseTaskFilters(searchParams);
      return toTaskServerFilter(
        // On the board the columns are the statuses
        view === "board" ? { ...filters, status: null } : filters,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchKey],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={view}
          onValueChange={(value) =>
            value &&
            setSearchParams(
              (params) =>
                setSearchParam(
                  params,
                  "view",
                  value === "list" ? "list" : null,
                ),
              { replace: true },
            )
          }
        >
          <ToggleGroupItem
            value="board"
            aria-label={translate("crm.operations.tasks.view_board")}
          >
            <KanbanSquare />
            {translate("crm.operations.tasks.view_board")}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="list"
            aria-label={translate("crm.operations.tasks.view_list")}
          >
            <List />
            {translate("crm.operations.tasks.view_list")}
          </ToggleGroupItem>
        </ToggleGroup>
        <TaskFilters showStatus={view === "list"} />
        <Button
          size="sm"
          className="ml-auto h-8"
          onClick={() => setDialog({ open: true })}
        >
          <Plus />
          {translate("crm.operations.tasks.create")}
        </Button>
      </div>

      {view === "board" ? (
        <TaskBoard
          filter={serverFilter}
          salesNames={salesNames}
          onOpen={(task) => setDialog({ open: true, task })}
        />
      ) : (
        <TaskList
          filter={serverFilter}
          salesNames={salesNames}
          onOpen={(task) => setDialog({ open: true, task })}
        />
      )}

      <TaskDialog
        open={dialog.open}
        task={dialog.open ? dialog.task : null}
        onClose={() => setDialog({ open: false })}
      />
    </div>
  );
};
